/**
 * Organizations: the MSPs and their Clients (Story 48, task 208), and per
 * Client its onboarding, revocation and per-source health (tasks 41, 46,
 * 62, 64; Stories 3, 10, 11). It was the Clients page; `/clients`
 * redirects here.
 *
 * Each MSP (and Simulint at the top) is a section: its name, what it may
 * resell, whether it uses indicators from other MSPs, and the moves the
 * caller holds there — New Client, New MSP and Rename with `manage_org` at
 * the node; Move, Suspend, Offboard, resale and the indicator switch only
 * from strictly above (an MSP does not do those to itself). Its own
 * Clients follow as the table this page always had. Before the tree exists
 * on an environment the page is that one table, as it was.
 *
 * Defender is connected by admin consent (the invite link). Credentials
 * for token integrations, and every per-source control, live on the
 * Integrations pages since task 69 — this page is about the Client as a
 * whole: add one, re-mint its invite, revoke it, and (Story 19) its
 * response settings and consent coverage. Who sees which control follows
 * the Story 22 matrix: adding a Client is `manage_org` where it goes,
 * revoking one is `manage_org` above it; the per-Client moves — response settings,
 * re-consent, re-mint — are `manage_client` on that Client. The API refuses
 * the rest anyway; hiding the buttons just spares the 403.
 */

import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import {
  createOrg,
  createTenant,
  describeError,
  listIntegrations,
  listTenants,
  reconsentTenant,
  startSlackInstall,
  reissueInvite,
  putResellable,
  revokeTenant,
  updateOrg,
  updateOutreachSettings,
  updateResponseSettings,
  type BillableState,
  type ClientTenant,
  type CreateOrgBody,
  type CreateOrgResult,
  type CreateTenantResult,
  type OrgNode,
  type UpdateOrgBody,
  type ReconsentResult,
  type OutreachSettingsUpdate,
  type ResponseSettingsUpdate,
  type ServiceKey,
} from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { useAccessToken } from '../auth/useAccessToken';
import { useOrgAdmin, type OrgAdmin } from '../auth/useOrgAdmin';
import { useSelectedTenant } from '../auth/useSelectedTenant';
import { useTenant } from '../auth/useTenant';
import { ConfirmModal } from '../components/ConfirmModal';
import { InviteModal } from '../components/InviteModal';
import {
  IndicatorsModal,
  MoveOrgModal,
  NewMspModal,
  RenameOrgModal,
  ResaleModal,
} from '../components/OrgModals';
import {
  ErrorNote,
  INPUT,
  ModalShell,
  timeAgo,
} from '@bluesphere/mdr-alert-panel';
import { ReconsentModal } from '../components/ReconsentModal';
import { OutreachSettingsModal } from '../components/OutreachSettingsModal';
import { ResponseSettingsModal, responseSummary } from '../components/ResponseSettingsModal';
import { SourceLine } from '../components/SourceHealth';
import { StatusBadge } from '../components/StatusBadge';
import {
  depthOf,
  isParentKind,
  orgLabel,
  pathTo,
  resellableAbove,
  SERVICE_KEYS,
  SERVICE_LABELS,
  serviceLabel,
  subtreeIds,
  type OrgIndex,
} from '../lib/orgs';

/** One organization move at a time, with the node it is about. */
type OrgAction =
  | {
      kind: 'new-msp' | 'rename' | 'resale' | 'indicators' | 'suspend' | 'reactivate' | 'offboard';
      node: OrgNode;
    }
  | { kind: 'move'; node: OrgNode; label: string };

export function Organizations() {
  const idToken = useAccessToken();
  const { can } = useTenant();
  const { orgs } = useSelectedTenant();
  const orgAdmin = useOrgAdmin();
  const treeMode = !orgAdmin.preTree;
  const queryClient = useQueryClient();

  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => (await listTenants(idToken)).tenants,
    enabled: Boolean(idToken),
    refetchInterval: 15_000, // surface consent transitions and health without manual refresh
  });

  const refreshTenants = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
  }, [queryClient]);
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
    void queryClient.invalidateQueries({ queryKey: ['orgs'] });
  };

  // Where a new Client goes: an organization's id, or 'flat' before the tree.
  const [addUnder, setAddUnder] = useState<string | null>(null);
  const [orgAction, setOrgAction] = useState<OrgAction | null>(null);
  const [activeInvite, setActiveInvite] = useState<CreateTenantResult | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<ClientTenant | null>(null);
  // Revoked Clients are history, not work: hidden unless asked for (task 62).
  const [showRevoked, setShowRevoked] = useState(false);
  const [serviceFilter, setServiceFilter] = useState<ServiceKey | ''>('');
  const [responseFor, setResponseFor] = useState<ClientTenant | null>(null);
  const [outreachFor, setOutreachFor] = useState<ClientTenant | null>(null);
  const [reconsent, setReconsent] = useState<{
    tenant: ClientTenant;
    result: ReconsentResult;
  } | null>(null);

  // Whether any registered integration declares a user action: the Users
  // switch is shown disabled until an identity connector exists.
  const integrations = useQuery({
    queryKey: ['integrations'],
    queryFn: () => listIntegrations(idToken),
    enabled: Boolean(idToken && can('manage_client')),
    staleTime: 5 * 60_000,
  });
  const userActionsAvailable = (integrations.data?.integrations ?? []).some(
    (i) => (i.manifest.actions.user ?? []).length > 0,
  );

  const createMutation = useMutation<
    CreateTenantResult,
    Error,
    { displayName: string; parentOrgId?: string }
  >({
    mutationFn: ({ displayName, parentOrgId }) =>
      createTenant(displayName, idToken, { parentOrgId }),
    onSuccess: (data) => {
      setActiveInvite(data);
      setAddUnder(null);
      invalidate();
    },
  });

  // ----- Organization moves (Story 48) -----
  const closeOrgAction = () => {
    setOrgAction(null);
    createOrgMutation.reset();
    updateOrgMutation.reset();
    resaleMutation.reset();
  };
  const createOrgMutation = useMutation<CreateOrgResult, Error, CreateOrgBody>({
    mutationFn: (body) => createOrg(body, idToken),
    onSuccess: () => {
      setOrgAction(null);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
  const updateOrgMutation = useMutation<OrgNode, Error, { orgId: string; body: UpdateOrgBody }>({
    mutationFn: ({ orgId, body }) => updateOrg(orgId, body, idToken),
    onSuccess: () => {
      setOrgAction(null);
      invalidate();
    },
  });
  const resaleMutation = useMutation<OrgNode, Error, { orgId: string; services: string[] }>({
    mutationFn: ({ orgId, services }) => putResellable(orgId, services, idToken),
    onSuccess: () => {
      setOrgAction(null);
      invalidate();
    },
  });

  const revokeMutation = useMutation<ClientTenant, Error, { clientTenantId: string }>({
    mutationFn: ({ clientTenantId }) => revokeTenant(clientTenantId, idToken),
    onSuccess: () => {
      setPendingRevoke(null);
      invalidate();
    },
  });

  // Re-mint (task 64): the old link dies, the new one shows once, as on create.
  const reissueMutation = useMutation<CreateTenantResult, Error, { clientTenantId: string }>({
    mutationFn: ({ clientTenantId }) => reissueInvite(clientTenantId, idToken),
    onSuccess: (data) => {
      setActiveInvite(data);
      invalidate();
    },
  });

  const settingsMutation = useMutation<
    ClientTenant,
    Error,
    { clientTenantId: string; body: ResponseSettingsUpdate }
  >({
    mutationFn: ({ clientTenantId, body }) => updateResponseSettings(clientTenantId, body, idToken),
    onSuccess: () => {
      setResponseFor(null);
      invalidate();
    },
  });
  const outreachMutation = useMutation<
    ClientTenant,
    Error,
    { clientTenantId: string; body: OutreachSettingsUpdate }
  >({
    mutationFn: ({ clientTenantId, body }) => updateOutreachSettings(clientTenantId, body, idToken),
    onSuccess: () => {
      setOutreachFor(null);
      invalidate();
    },
  });
  const reconsentMutation = useMutation<ReconsentResult, Error, { tenant: ClientTenant }>({
    mutationFn: ({ tenant }) => reconsentTenant(tenant.client_tenant_id, idToken),
    onSuccess: (result, { tenant }) => setReconsent({ tenant, result }),
  });

  const allTenants = tenantsQuery.data ?? [];
  const revokedCount = allTenants.filter((t) => t.status === 'revoked').length;
  const unfiltered = showRevoked ? allTenants : allTenants.filter((t) => t.status !== 'revoked');
  // Story 45's `service` filter and count (task 179c).
  const serviceCounts = Object.fromEntries(
    SERVICE_KEYS.map((key) => [key, unfiltered.filter((t) => t.services.includes(key)).length]),
  ) as Record<ServiceKey, number>;
  const visibleTenants = serviceFilter
    ? unfiltered.filter((t) => t.services.includes(serviceFilter))
    : unfiltered;

  // Revoking is `manage_org` above the Client: anyone holding it at the
  // Client's parent or higher (task 205). Before the tree: manage_platform.
  const canRevoke = (tenantId: string) => (treeMode ? orgAdmin.above(tenantId) : orgAdmin.holds);
  const homeNode = orgs.homeId ? orgs.byId.get(orgs.homeId) : undefined;
  const addTarget = treeMode ? homeNode : undefined;
  const canAddAtTop = treeMode ? Boolean(homeNode && orgAdmin.at(homeNode.org_id)) : orgAdmin.holds;

  // Where a node may move: an organization the caller manages, outside the
  // node's own subtree (the API refuses a cycle anyway).
  const moveCandidates = (nodeId: string) => {
    const own = subtreeIds(orgs, nodeId);
    const top = orgs.homeId ? depthOf(orgs, orgs.homeId) : 0;
    return orgs.ordered
      .filter(
        (n) =>
          isParentKind(n) && n.status === 'active' && orgAdmin.at(n.org_id) && !own.has(n.org_id),
      )
      .map((n) => ({ node: n, depth: depthOf(orgs, n.org_id) - top }));
  };

  // Suspend, reactivate and offboard share one confirm.
  const statusAction =
    orgAction &&
    (orgAction.kind === 'suspend' ||
      orgAction.kind === 'reactivate' ||
      orgAction.kind === 'offboard')
      ? { kind: orgAction.kind, node: orgAction.node }
      : null;
  const STATUS_COPY = {
    suspend: {
      title: 'Suspend',
      to: 'suspended',
      body: 'Its users, and the users of every MSP beneath it, lose access within a minute. Monitoring of its Clients carries on, and Admins above it still see them.',
    },
    reactivate: {
      title: 'Reactivate',
      to: 'active',
      body: 'Its users regain access within a minute. It needs an enabled Admin of its own.',
    },
    offboard: {
      title: 'Offboard',
      to: 'offboarded',
      body: 'Final: its users lose access for good. Offboard or move its MSPs and Clients first; the API refuses while any is still active.',
    },
  } as const;

  const tableProps = {
    isLoading: tenantsQuery.isLoading,
    error: tenantsQuery.error,
    canManage: (tenantId: string) => can('manage_client', tenantId),
    canRevoke,
    onRevoke: setPendingRevoke,
    onReissueInvite: (tenant: ClientTenant) =>
      reissueMutation.mutate({ clientTenantId: tenant.client_tenant_id }),
    reissuePendingFor: reissueMutation.isPending
      ? reissueMutation.variables?.clientTenantId
      : undefined,
    reissueError: reissueMutation.error ?? reconsentMutation.error,
    onResponseSettings: setResponseFor,
    onOutreachSettings: setOutreachFor,
    onReconsent: (tenant: ClientTenant) => reconsentMutation.mutate({ tenant }),
    reconsentPendingFor: reconsentMutation.isPending
      ? reconsentMutation.variables?.tenant.client_tenant_id
      : undefined,
    canMove: (tenantId: string) => treeMode && orgAdmin.above(tenantId),
    onMove: (tenant: ClientTenant) => {
      const node = orgs.byId.get(tenant.client_tenant_id);
      if (node) setOrgAction({ kind: 'move', node, label: tenant.display_name });
    },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Organizations</h1>
          <p className="mt-1 text-sm text-white/60">
            {treeMode &&
              'MSPs and the Clients they serve. An Admin manages their own organization and everything beneath it. '}
            Client tenants onboarded to BlueSphere MDR. Defender connects through admin consent in
            the Client's own Microsoft Entra tenant. Credentials for the other sources are managed
            per integration on the{' '}
            <Link to="/integrations" className="underline">
              Integrations
            </Link>{' '}
            page. Revoking stops ingestion on the next tick and deletes any stored credential.
          </p>
        </div>
        {canAddAtTop && (
          <PillButton onClick={() => setAddUnder(addTarget?.org_id ?? 'flat')}>
            Add a Client
          </PillButton>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-white/70">
        <span>
          {visibleTenants.length} Client{visibleTenants.length === 1 ? '' : 's'}
          {!showRevoked && revokedCount > 0 && ` · ${revokedCount} revoked hidden`}
        </span>
        <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2">
          <span>Service</span>
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value as ServiceKey | '')}
            className="rounded-input border border-white/20 bg-white/10 px-2 py-1 text-white"
          >
            <option value="">All ({unfiltered.length})</option>
            {SERVICE_KEYS.map((key) => (
              <option key={key} value={key}>
                {SERVICE_LABELS[key]} ({serviceCounts[key]})
              </option>
            ))}
          </select>
        </label>
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={showRevoked}
            onChange={(e) => setShowRevoked(e.target.checked)}
            className="h-4 w-4 rounded-sm border-white/30 bg-white/10"
          />
          Show revoked Clients
        </label>
        </div>
      </div>

      {treeMode ? (
        <div className="space-y-6">
          {orgs.ordered
            .filter((n) => isParentKind(n) && (showRevoked || n.status !== 'offboarded'))
            .map((node) => (
              <OrgSection
                key={node.org_id}
                node={node}
                orgs={orgs}
                orgAdmin={orgAdmin}
                tenants={visibleTenants.filter(
                  (t) => orgs.byId.get(t.client_tenant_id)?.parent_id === node.org_id,
                )}
                onAddClient={() => setAddUnder(node.org_id)}
                onAction={(kind) =>
                  setOrgAction(
                    kind === 'move' ? { kind, node, label: orgLabel(node) } : { kind, node },
                  )
                }
                tableProps={tableProps}
              />
            ))}
        </div>
      ) : (
        <TenantsTable
          tenants={visibleTenants}
          canCreate={orgAdmin.holds}
          emptyBecauseFiltered={Boolean(serviceFilter) || (!showRevoked && revokedCount > 0)}
          {...tableProps}
        />
      )}

      {orgAction?.kind === 'new-msp' && (
        <NewMspModal
          parent={orgAction.node}
          onSubmit={(body) => createOrgMutation.mutate(body)}
          onCancel={closeOrgAction}
          isPending={createOrgMutation.isPending}
          error={createOrgMutation.error}
        />
      )}
      {orgAction?.kind === 'rename' && (
        <RenameOrgModal
          node={orgAction.node}
          onSubmit={(name) =>
            updateOrgMutation.mutate({ orgId: orgAction.node.org_id, body: { name } })
          }
          onCancel={closeOrgAction}
          isPending={updateOrgMutation.isPending}
          error={updateOrgMutation.error}
        />
      )}
      {orgAction?.kind === 'move' && (
        <MoveOrgModal
          label={orgAction.label}
          currentParentId={orgAction.node.parent_id}
          candidates={moveCandidates(orgAction.node.org_id)}
          onSubmit={(parentId) =>
            updateOrgMutation.mutate({
              orgId: orgAction.node.org_id,
              body: { parent_id: parentId },
            })
          }
          onCancel={closeOrgAction}
          isPending={updateOrgMutation.isPending}
          error={updateOrgMutation.error}
        />
      )}
      {orgAction?.kind === 'resale' && (
        <ResaleModal
          node={orgAction.node}
          allowed={resellableAbove(orgs, orgAction.node)}
          onSubmit={(services) => resaleMutation.mutate({ orgId: orgAction.node.org_id, services })}
          onCancel={closeOrgAction}
          isPending={resaleMutation.isPending}
          error={resaleMutation.error}
        />
      )}
      {orgAction?.kind === 'indicators' && (
        <IndicatorsModal
          node={orgAction.node}
          onSubmit={(value) =>
            updateOrgMutation.mutate({
              orgId: orgAction.node.org_id,
              body: { consume_across_msps: value },
            })
          }
          onCancel={closeOrgAction}
          isPending={updateOrgMutation.isPending}
          error={updateOrgMutation.error}
        />
      )}
      {statusAction && (
        <ConfirmModal
          title={`${STATUS_COPY[statusAction.kind].title} ${orgLabel(statusAction.node)}?`}
          confirmLabel={STATUS_COPY[statusAction.kind].title}
          pendingLabel="Saving…"
          onCancel={closeOrgAction}
          onConfirm={() =>
            updateOrgMutation.mutate({
              orgId: statusAction.node.org_id,
              body: { status: STATUS_COPY[statusAction.kind].to },
            })
          }
          isPending={updateOrgMutation.isPending}
          error={updateOrgMutation.error}
        >
          <p className="text-sm text-bluesphere-textSecondary">
            {STATUS_COPY[statusAction.kind].body}
          </p>
        </ConfirmModal>
      )}

      {outreachFor && (
        <OutreachSettingsModal
          tenant={
            tenantsQuery.data?.find((t) => t.client_tenant_id === outreachFor.client_tenant_id) ??
            outreachFor
          }
          onCancel={() => {
            setOutreachFor(null);
            outreachMutation.reset();
          }}
          onSubmit={(body) =>
            outreachMutation.mutate({ clientTenantId: outreachFor.client_tenant_id, body })
          }
          onRefresh={refreshTenants}
          onConnectSlack={() =>
            startSlackInstall(outreachFor.client_tenant_id, idToken).then((r) => r.install_url)
          }
          isPending={outreachMutation.isPending}
          error={outreachMutation.error}
        />
      )}

      {responseFor && (
        <ResponseSettingsModal
          tenant={responseFor}
          userActionsAvailable={userActionsAvailable}
          onCancel={() => {
            setResponseFor(null);
            settingsMutation.reset();
          }}
          onSubmit={(body) =>
            settingsMutation.mutate({ clientTenantId: responseFor.client_tenant_id, body })
          }
          isPending={settingsMutation.isPending}
          error={settingsMutation.error}
        />
      )}

      {reconsent && (
        <ReconsentModal
          displayName={reconsent.tenant.display_name}
          result={reconsent.result}
          onClose={() => {
            setReconsent(null);
            reconsentMutation.reset();
          }}
        />
      )}

      {addUnder && (
        <AddTenantModal
          parentName={addUnder === 'flat' ? null : orgLabel(orgs.byId.get(addUnder))}
          onCancel={() => {
            setAddUnder(null);
            createMutation.reset();
          }}
          onSubmit={(displayName) =>
            createMutation.mutate({
              displayName,
              parentOrgId: addUnder === 'flat' ? undefined : addUnder,
            })
          }
          isPending={createMutation.isPending}
          error={createMutation.error}
        />
      )}

      {activeInvite && (
        <InviteModal
          tenant={activeInvite}
          onClose={() => {
            setActiveInvite(null);
            createMutation.reset();
            reissueMutation.reset();
          }}
        />
      )}

      {pendingRevoke && (
        <ConfirmModal
          title="Revoke access?"
          confirmLabel="Revoke access"
          pendingLabel="Revoking…"
          onCancel={() => {
            setPendingRevoke(null);
            revokeMutation.reset();
          }}
          onConfirm={() =>
            revokeMutation.mutate({ clientTenantId: pendingRevoke.client_tenant_id })
          }
          isPending={revokeMutation.isPending}
          error={revokeMutation.error}
        >
          <p className="text-sm text-bluesphere-textSecondary">
            BlueSphere will stop ingesting telemetry from{' '}
            <strong className="text-bluesphere-textOnLight">{pendingRevoke.display_name}</strong>{' '}
            and delete its stored SentinelOne credential. Detections already ingested stay visible,
            marked revoked.
          </p>
          <p className="text-xs text-bluesphere-textSecondary">
            <strong className="text-bluesphere-textOnLight">
              Full revocation is a Client-side action.
            </strong>{' '}
            To remove BlueSphere's app from their Entra tenant, the Client admin deletes it under
            Enterprise applications in their own portal.
          </p>
        </ConfirmModal>
      )}
    </div>
  );
}

// ----- Tenants table -------------------------------------------------------

function TenantsTable({
  tenants,
  isLoading,
  error,
  canCreate,
  canManage,
  canRevoke,
  emptyBecauseFiltered,
  onRevoke,
  onReissueInvite,
  reissuePendingFor,
  reissueError,
  onResponseSettings,
  onOutreachSettings,
  onReconsent,
  reconsentPendingFor,
  canMove,
  onMove,
}: {
  tenants: ClientTenant[];
  isLoading: boolean;
  error: unknown;
  canCreate: boolean;
  /** `manage_client` on this Client. */
  canManage: (tenantId: string) => boolean;
  /** `manage_org` above the Client: revoking deletes it. */
  canRevoke: (tenantId: string) => boolean;
  /** `manage_org` above the Client, once the tree exists (Story 48). */
  canMove: (tenantId: string) => boolean;
  onMove: (tenant: ClientTenant) => void;
  emptyBecauseFiltered: boolean;
  onRevoke: (tenant: ClientTenant) => void;
  onReissueInvite: (tenant: ClientTenant) => void;
  reissuePendingFor?: string;
  reissueError: unknown;
  onResponseSettings: (tenant: ClientTenant) => void;
  onOutreachSettings: (tenant: ClientTenant) => void;
  onReconsent: (tenant: ClientTenant) => void;
  reconsentPendingFor?: string;
}) {
  if (isLoading) {
    return <div className="bs-card p-8 text-bluesphere-textSecondary">Loading Clients…</div>;
  }
  if (error) {
    return (
      <div className="bs-card border border-red-200 bg-red-50 p-6" role="alert">
        <div className="text-sm font-semibold text-red-900">Could not load Clients</div>
        <div className="mt-1 font-mono text-xs text-red-800">{describeError(error)}</div>
      </div>
    );
  }
  if (tenants.length === 0) {
    return (
      <div className="bs-card p-8 text-center text-bluesphere-textSecondary">
        <div className="text-base font-semibold text-bluesphere-textOnLight">
          {emptyBecauseFiltered ? 'No active Clients' : 'No Clients yet'}
        </div>
        <div className="mt-2 text-sm">
          {emptyBecauseFiltered ? (
            'Every Client is revoked. Tick "Show revoked Clients" to see them.'
          ) : canCreate ? (
            <>
              Click <strong>Add a Client</strong> to mint an invite link.
            </>
          ) : (
            'No Clients are assigned to your account.'
          )}
        </div>
      </div>
    );
  }

  const sorted = [...tenants].sort((a, b) =>
    b.invite_created_at.localeCompare(a.invite_created_at),
  );

  // The Actions column exists when the caller can act on any listed Client;
  // each row then offers only the moves held on that Client.
  const hasActions = sorted.some(
    (t) => canRevoke(t.client_tenant_id) || canManage(t.client_tenant_id),
  );
  const actionsFor = (tenant: ClientTenant) =>
    hasActions ? (
      <TenantActions
        tenant={tenant}
        canManage={canManage(tenant.client_tenant_id)}
        canRevoke={canRevoke(tenant.client_tenant_id)}
        canMove={canMove(tenant.client_tenant_id)}
        onMove={onMove}
        onRevoke={onRevoke}
        onReissueInvite={onReissueInvite}
        reissuePending={reissuePendingFor === tenant.client_tenant_id}
        onResponseSettings={onResponseSettings}
        onOutreachSettings={onOutreachSettings}
        onReconsent={onReconsent}
        reconsentPending={reconsentPendingFor === tenant.client_tenant_id}
      />
    ) : null;

  return (
    <div className="bs-card">
      {reissueError != null && (
        <div className="px-4 pt-4">
          <ErrorNote error={reissueError} />
        </div>
      )}

      {/* Phone: one card per Client, nothing to scroll sideways (task 75). */}
      <ul className="divide-y divide-bluesphere-border md:hidden">
        {sorted.map((tenant) => (
          <li key={tenant.client_tenant_id} className="space-y-2 px-4 py-4 text-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-bluesphere-textOnLight">{tenant.display_name}</div>
                <div className="mt-0.5 break-all font-mono text-xs text-bluesphere-muted">
                  {tenant.client_tenant_id}
                </div>
              </div>
              <StatusBadge status={tenant.status} />
            </div>
            <ServicesCell tenant={tenant} />
            <SourcesCell tenant={tenant} />
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-bluesphere-textSecondary">Billing:</span>
              <BillingCell tenant={tenant} />
            </div>
            <ResponseCell tenant={tenant} />
            <div
              className="text-xs text-bluesphere-textSecondary"
              title={tenant.consented_at ?? undefined}
            >
              {tenant.consented_at
                ? `Consented ${timeAgo(tenant.consented_at)}`
                : tenant.status === 'active'
                  ? 'No consent needed'
                  : 'Not yet consented'}
            </div>
            {hasActions && <div className="pt-1">{actionsFor(tenant)}</div>}
          </li>
        ))}
      </ul>

      {/* Desktop: the table. `overflow-x-auto` stays as a safety net only here. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead className="border-b border-bluesphere-border bg-bluesphere-surfaceMuted">
            <tr className="text-left text-xs uppercase tracking-wider text-bluesphere-textSecondary">
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Services</th>
              <th className="px-4 py-3">Sources</th>
              <th className="px-4 py-3">Billing</th>
              <th className="px-4 py-3">Response</th>
              <th className="px-4 py-3">Consented</th>
              {hasActions && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sorted.map((tenant) => (
              <tr
                key={tenant.client_tenant_id}
                className="border-t border-bluesphere-border align-top"
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-bluesphere-textOnLight">
                    {tenant.display_name}
                  </div>
                  <div className="mt-0.5 font-mono text-xs text-bluesphere-muted">
                    {tenant.client_tenant_id}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={tenant.status} />
                </td>
                <td className="px-4 py-3">
                  <ServicesCell tenant={tenant} />
                </td>
                <td className="px-4 py-3">
                  <SourcesCell tenant={tenant} />
                </td>
                <td className="px-4 py-3">
                  <BillingCell tenant={tenant} />
                </td>
                <td className="px-4 py-3">
                  <ResponseCell tenant={tenant} />
                </td>
                <td
                  className="px-4 py-3 text-xs text-bluesphere-textSecondary"
                  title={tenant.consented_at ?? undefined}
                >
                  {tenant.consented_at ? timeAgo(tenant.consented_at) : '—'}
                </td>
                {hasActions && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap items-center justify-end gap-3">
                      {actionsFor(tenant)}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * The actions for one Client — the Client as a whole, not its sources
 * (those are on the Integrations pages). One component for the table cell
 * and the phone card, so the two can never offer different moves.
 * `canManage` gates the per-Client moves, `canRevoke` the deletion.
 */
function TenantActions({
  tenant,
  canManage,
  canRevoke,
  canMove,
  onMove,
  onRevoke,
  onReissueInvite,
  reissuePending,
  onResponseSettings,
  onOutreachSettings,
  onReconsent,
  reconsentPending,
}: {
  tenant: ClientTenant;
  canManage: boolean;
  canRevoke: boolean;
  canMove: boolean;
  onMove: (tenant: ClientTenant) => void;
  onRevoke: (tenant: ClientTenant) => void;
  onReissueInvite: (tenant: ClientTenant) => void;
  reissuePending: boolean;
  onResponseSettings: (tenant: ClientTenant) => void;
  onOutreachSettings: (tenant: ClientTenant) => void;
  onReconsent: (tenant: ClientTenant) => void;
  reconsentPending: boolean;
}) {
  const plain = 'text-xs text-bluesphere-textOnLight underline hover:text-bluesphere-secondary';
  const danger = 'text-xs text-red-700 underline hover:text-red-900';
  return (
    <div className="flex flex-wrap items-center gap-3 md:justify-end">
      {canManage && tenant.status === 'consented' && (
        <button
          type="button"
          onClick={() => onResponseSettings(tenant)}
          className={plain}
          title="Whether BlueSphere may contain threats on this Client, and how."
        >
          Response settings
        </button>
      )}
      {canManage && tenant.status === 'consented' && (
        <button
          type="button"
          onClick={() => onOutreachSettings(tenant)}
          className={plain}
          title="Whether BlueSphere may ask this Client's users whether an alert was them."
        >
          Outreach settings
        </button>
      )}
      {canManage && tenant.status === 'consented' && tenant.missing_scopes.length > 0 && (
        <button
          type="button"
          onClick={() => onReconsent(tenant)}
          disabled={reconsentPending}
          className={`${plain} disabled:opacity-60`}
          title={`Mint a consent link that adds: ${tenant.missing_scopes.join(', ')}`}
        >
          {reconsentPending ? 'Minting…' : 'Update permissions'}
        </button>
      )}
      {canManage && tenant.status === 'pending' && (
        <button
          type="button"
          onClick={() => onReissueInvite(tenant)}
          disabled={reissuePending}
          className={`${plain} disabled:opacity-60`}
          title="Mint a fresh consent link. The previous link stops working."
        >
          {reissuePending ? 'Minting…' : 'Re-mint invite'}
        </button>
      )}
      {canMove && tenant.status !== 'revoked' && (
        <button
          type="button"
          onClick={() => onMove(tenant)}
          className={plain}
          title="Put this Client under another organization you manage."
        >
          Move
        </button>
      )}
      {canRevoke && tenant.status !== 'revoked' && (
        <button type="button" onClick={() => onRevoke(tenant)} className={danger}>
          Revoke
        </button>
      )}
    </div>
  );
}

/**
 * The Usage Unit count in one line (Story 21): the number and its age, or
 * "—" with the reason there is no number. What we bill and what the
 * platform sees must be the same figure, so a stale or absent count is
 * shown as exactly that rather than hidden.
 */
function BillableCell({ tenant }: { tenant: ClientTenant }) {
  const reasons: Record<Exclude<BillableState, 'ok'>, string> = {
    no_idp: 'No identity provider connected',
    missing_permission: 'Consent lacks the permission — use "Update permissions"',
    error: 'Count failed; will retry',
  };
  if (tenant.billable_users === null && tenant.billable_state === null) {
    return <span className="text-xs text-bluesphere-muted">—</span>;
  }
  const breakdown = tenant.billable_breakdown;
  const excluded = breakdown ? (breakdown.disabled ?? 0) + (breakdown.unlicensed ?? 0) : 0;
  return (
    <div className="text-xs">
      {tenant.billable_users !== null && (
        <div
          className="text-bluesphere-textOnLight"
          title={
            breakdown
              ? `Excluded: ${breakdown.disabled ?? 0} disabled, ${breakdown.unlicensed ?? 0} unlicensed`
              : undefined
          }
        >
          <span className="text-sm font-semibold">{tenant.billable_users}</span>
          {excluded > 0 && (
            <span className="text-bluesphere-textSecondary"> +{excluded} excl.</span>
          )}
          {tenant.billable_counted_at && (
            <span className="text-bluesphere-textSecondary" title={tenant.billable_counted_at}>
              {' '}
              · {timeAgo(tenant.billable_counted_at)}
            </span>
          )}
        </div>
      )}
      {tenant.billable_users === null && <div className="text-bluesphere-muted">—</div>}
      {tenant.billable_state !== null && tenant.billable_state !== 'ok' && (
        <div className="text-amber-800" title={tenant.billable_error ?? undefined}>
          {reasons[tenant.billable_state]}
        </div>
      )}
    </div>
  );
}

/** The services this Client is engaged for, as chips (Story 45, task 179c). */
function ServicesCell({ tenant }: { tenant: ClientTenant }) {
  return (
    <div className="flex flex-wrap gap-1">
      {[...tenant.services].sort().map((key) => (
        <span
          key={key}
          className="whitespace-nowrap rounded-full bg-bluesphere-surfaceMuted px-2 py-0.5 text-xs text-bluesphere-textOnLight"
        >
          {serviceLabel(key)}
        </span>
      ))}
    </div>
  );
}

/**
 * What the Client bills on, one line per product line (design r12
 * "Billing"): the Usage Unit count, then LatticeAI DLP's own meter —
 * protected devices this month, or per Usage Unit when a Usage-Unit
 * service covers the bill (a Lattice add-on beside it, never folded in).
 */
function BillingCell({ tenant }: { tenant: ClientTenant }) {
  const dlp = tenant.services.includes('latticeai_dlp');
  const dlpMeter = tenant.meter.latticeai_dlp;
  return (
    <div className="space-y-1">
      <BillableCell tenant={tenant} />
      {dlp && (
        <div className="text-xs text-bluesphere-textSecondary">
          {serviceLabel('latticeai_dlp')}:{' '}
          {dlpMeter === 'usage_unit' ? (
            <span>per Usage Unit (add-on)</span>
          ) : tenant.protected_devices ? (
            <span>
              <span className="font-semibold text-bluesphere-textOnLight">
                {tenant.protected_devices.count}
              </span>{' '}
              protected device{tenant.protected_devices.count === 1 ? '' : 's'} ·{' '}
              {monthLabel(tenant.protected_devices.month)}
            </span>
          ) : (
            <span>—</span>
          )}
        </div>
      )}
    </div>
  );
}

function monthLabel(month: string): string {
  const [year, m] = month.split('-').map(Number);
  if (!year || !m) return month;
  return new Date(Date.UTC(year, m - 1, 1)).toLocaleString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** The Client's response posture in one line (Story 19). */
function ResponseCell({ tenant }: { tenant: ClientTenant }) {
  if (tenant.status !== 'consented') {
    return <span className="text-xs text-bluesphere-muted">—</span>;
  }
  const summary = responseSummary(tenant);
  const missing = tenant.missing_scopes.length;
  return (
    <div className="text-xs">
      <div className={summary === 'off' ? 'text-bluesphere-muted' : 'text-bluesphere-textOnLight'}>
        {summary === 'off' ? 'Response off' : summary}
      </div>
      {missing > 0 && (
        <div className="text-amber-800" title={tenant.missing_scopes.join(', ')}>
          {missing} permission{missing === 1 ? '' : 's'} not yet granted
        </div>
      )}
    </div>
  );
}

/** Each source's health, its name linking to the integration page that manages it. */
function SourcesCell({ tenant }: { tenant: ClientTenant }) {
  const entries = Object.entries(tenant.sources);
  if (entries.length === 0) {
    return (
      <span className="text-xs text-bluesphere-muted">
        {tenant.status === 'consented' ? (
          'Defender (awaiting first sync)'
        ) : tenant.status === 'pending' ? (
          <>
            Awaiting consent ·{' '}
            <Link to="/integrations" className="underline">
              manage integrations
            </Link>
          </>
        ) : (
          '—'
        )}
      </span>
    );
  }
  return (
    <div className="space-y-1.5">
      {entries.map(([name, info]) => (
        <SourceLine key={name} name={name} info={info} linkToIntegration />
      ))}
    </div>
  );
}

// ----- One organization (Story 48) -----------------------------------------

type TableProps = Omit<
  Parameters<typeof TenantsTable>[0],
  'tenants' | 'canCreate' | 'emptyBecauseFiltered'
>;

/**
 * One MSP (or Simulint) and its own Clients. The header carries what the
 * caller may do here: at the node (`at`) New Client, New MSP and Rename;
 * from strictly above (`above`) Move, Suspend or Reactivate, Offboard,
 * resale and the indicator switch.
 */
function OrgSection({
  node,
  orgs,
  orgAdmin,
  tenants,
  onAddClient,
  onAction,
  tableProps,
}: {
  node: OrgNode;
  orgs: OrgIndex;
  orgAdmin: OrgAdmin;
  tenants: ClientTenant[];
  onAddClient: () => void;
  onAction: (kind: OrgAction['kind']) => void;
  tableProps: TableProps;
}) {
  const at = orgAdmin.at(node.org_id) && node.status === 'active';
  const above = orgAdmin.above(node.org_id);
  const depth = Math.max(
    0,
    depthOf(orgs, node.org_id) - (orgs.homeId ? depthOf(orgs, orgs.homeId) : 0),
  );
  const path = pathTo(orgs, node.org_id).slice(0, -1);
  const plain = 'text-xs text-white underline decoration-white/40 hover:decoration-white';
  const danger = 'text-xs text-red-200 underline hover:text-red-100';
  const resale = node.resellable ?? [];

  return (
    <section
      className="space-y-3"
      style={depth > 0 ? { marginLeft: `min(${depth * 1.5}rem, 12vw)` } : undefined}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-l-2 border-white/20 pl-3">
        <div className="min-w-0">
          {path.length > 0 && (
            <div className="truncate text-xs text-white/50">
              {path.map((p) => orgLabel(p)).join(' › ')}
            </div>
          )}
          <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold">
            {orgLabel(node)}
            <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/70">
              {node.kind === 'simulint' ? 'Simulint' : 'MSP'}
            </span>
            {node.status !== 'active' && (
              <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200">
                {node.status}
              </span>
            )}
          </h2>
          <div className="mt-0.5 text-xs text-white/60">
            Resells {resale.length > 0 ? resale.map(serviceLabel).join(', ') : 'nothing yet'}
            {node.kind === 'msp' && (
              <>
                {' · '}indicators from other MSPs {node.consumes_across_msps ? 'on' : 'off'}
                {node.consume_across_msps == null && ' (inherited)'}
              </>
            )}
            {' · '}
            {tenants.length} Client{tenants.length === 1 ? '' : 's'}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {at && (
            <>
              <button type="button" className={plain} onClick={onAddClient}>
                New Client
              </button>
              <button type="button" className={plain} onClick={() => onAction('new-msp')}>
                New MSP
              </button>
              <button type="button" className={plain} onClick={() => onAction('rename')}>
                Rename
              </button>
            </>
          )}
          {above && (
            <>
              <button type="button" className={plain} onClick={() => onAction('move')}>
                Move
              </button>
              <button type="button" className={plain} onClick={() => onAction('resale')}>
                Resellable services
              </button>
              <button type="button" className={plain} onClick={() => onAction('indicators')}>
                Use indicators from other MSPs
              </button>
              {node.status === 'active' ? (
                <button type="button" className={danger} onClick={() => onAction('suspend')}>
                  Suspend
                </button>
              ) : node.status === 'suspended' ? (
                <button type="button" className={plain} onClick={() => onAction('reactivate')}>
                  Reactivate
                </button>
              ) : null}
              {node.status !== 'offboarded' && (
                <button type="button" className={danger} onClick={() => onAction('offboard')}>
                  Offboard
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {tenants.length > 0 && (
        <TenantsTable
          tenants={tenants}
          canCreate={at}
          emptyBecauseFiltered={false}
          {...tableProps}
        />
      )}
    </section>
  );
}

// ----- Add a Client --------------------------------------------------------

function AddTenantModal({
  parentName,
  onCancel,
  onSubmit,
  isPending,
  error,
}: {
  /** The organization it goes under; null before the tree exists. */
  parentName: string | null;
  onCancel: () => void;
  onSubmit: (displayName: string) => void;
  isPending: boolean;
  error: unknown;
}) {
  const [displayName, setDisplayName] = useState('');
  const trimmed = displayName.trim();
  const canSubmit = trimmed.length > 0 && !isPending;

  return (
    <ModalShell onClose={onCancel}>
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
          {parentName ? `Add a Client to ${parentName}` : 'Add a Client'}
        </h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">
          Mint a consent link for a new Client tenant. The Client admin opens it, signs in to their
          own Microsoft Entra tenant, and grants BlueSphere read access to Defender detections,
          incidents and hunting tables.
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit(trimmed);
        }}
        className="space-y-4 px-6 py-5"
      >
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
            Client name
          </span>
          <input
            type="text"
            autoFocus
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Acme Corp"
            className={INPUT}
          />
        </label>
        <ErrorNote error={error} />
        <div className="flex items-center justify-end gap-2 pt-2">
          <PillButton variant="ghost" size="sm" type="button" onClick={onCancel}>
            Cancel
          </PillButton>
          <PillButton size="sm" type="submit" disabled={!canSubmit}>
            {isPending ? 'Creating…' : 'Mint invite'}
          </PillButton>
        </div>
      </form>
    </ModalShell>
  );
}
